import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApi } from "../Api/ApiContext";
import { useAsyncFn } from "react-use";
import Layout, { Main } from "../Layout";
import { Col, Row, Button } from "reactstrap";
import styled from "styled-components";
import { formatDate } from "../utils/DateFormater";
import useCelebritiesStore from "../store/CelebritiesStore";
import {
  CountBadge,
  EmptyState,
  NUMBER_ACCESSOR,
  Section,
  SectionHeader,
  SortableHeaders,
  StyledTable,
  TableWrapper,
  TEXT_ACCESSOR,
  useTableSort,
} from "../component/DataTable";

const HeaderActions = styled.div`
  margin-left: auto;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  .btn {
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
    padding: 0.3rem 1rem;
    white-space: nowrap;
  }
`;

const ProductName = styled.span`
  font-weight: 600;
  text-transform: capitalize;
`;

const COLUMNS = [
  { key: "name", label: "Nom" },
  { key: "price", label: "Prix" },
  { key: "currency", label: "Devise" },
  { key: "quantity", label: "Quantité" },
  { key: "launchDate", label: "Date de sortie" },
  { key: "endDate", label: "Fin des ventes" },
];

const SORT_ACCESSORS = {
  name: (product) => (product.category?.name || "").toLowerCase(),
  price: NUMBER_ACCESSOR("price"),
  currency: TEXT_ACCESSOR("currency"),
  quantity: NUMBER_ACCESSOR("quantity"),
  launchDate: (product) =>
    product.launchDate ? new Date(product.launchDate).getTime() || 0 : 0,
  endDate: (product) =>
    product.endDate ? new Date(product.endDate).getTime() || 0 : 0,
};

export default function ProductTable({ products }) {
  const navigate = useNavigate();
  const { deleteProduct } = useApi();
  const [{ loading }, deleteRequest] = useAsyncFn((id) => deleteProduct(id));

  const { celebrities } = useCelebritiesStore();
  const {
    sortedRows: sortedProducts,
    sort,
    toggleSort,
  } = useTableSort(products, SORT_ACCESSORS);

  const updateProduct = (e, product) => {
    e.preventDefault();
    navigate("/admin/product/update", {
      state: {
        product: product,
      },
    });
  };

  const handleDeleteProduct = (e, product) => {
    const result = window.confirm(
      products.length === 1
        ? "Vous êtes sur le point de supprimer tout vos produits. Si vous confirmer cette action, votre annonce sera désactivée."
        : "Êtes-vous sur de vouloir supprimer ce produit ?"
    );
    if (result) {
      deleteRequest([product.id]).then((res) => {
        window.location.reload();
      });
    }
  };

  return (
    <Layout celebrities={celebrities.items} backPath={{ pathname: "/admin" }}>
      <Main>
        <div className={"my-3"}>
          <Section>
            <SectionHeader>
              <h1>Vos produits</h1>
              <CountBadge>{products.length}</CountBadge>
              <HeaderActions>
                <Button
                  tag={Link}
                  to={"/admin/product/new"}
                  color={"primary"}
                  size={"sm"}
                  className={"rounded-pill px-4 fw-semibold"}
                  disabled={products.length > 2}
                >
                  Ajouter un produit
                </Button>
              </HeaderActions>
            </SectionHeader>

            <Row>
              <Col sm={12}>
                <TableWrapper>
                  <StyledTable>
                    <thead>
                      <SortableHeaders
                        columns={COLUMNS}
                        sort={sort}
                        onSort={toggleSort}
                      >
                        <th scope={"col"}>Actions</th>
                      </SortableHeaders>
                    </thead>
                    <tbody>
                      {!sortedProducts || sortedProducts.length === 0 ? (
                        <tr>
                          <td colSpan={7}>
                            <EmptyState>Aucun produit n'a été crée.</EmptyState>
                          </td>
                        </tr>
                      ) : (
                        sortedProducts.map((product, index) => (
                          <tr key={product.id || index}>
                            <td data-label={"Nom"}>
                              <ProductName>{product.category.name}</ProductName>
                            </td>
                            <td data-label={"Prix"}>{product.price}</td>
                            <td data-label={"Devise"}>{product.currency}</td>
                            <td data-label={"Quantité"}>{product.quantity}</td>
                            <td data-label={"Date de sortie"}>
                              {product.launchDate
                                ? formatDate(
                                    new Date(product.launchDate),
                                    "dd-MM-yyyy"
                                  )
                                : "-"}
                            </td>
                            <td data-label={"Fin des ventes"}>
                              {product.endDate
                                ? formatDate(
                                    new Date(product.endDate),
                                    "dd-MM-yyyy"
                                  )
                                : "-"}
                            </td>
                            <td data-label={"Actions"}>
                              <RowActions>
                                <Button
                                  size={"sm"}
                                  color={"primary"}
                                  disabled={loading}
                                  onClick={(e) => updateProduct(e, product)}
                                >
                                  Éditer
                                </Button>
                                <Button
                                  size={"sm"}
                                  color={"danger"}
                                  disabled={loading}
                                  onClick={(e) =>
                                    handleDeleteProduct(e, product)
                                  }
                                >
                                  Supprimer
                                </Button>
                              </RowActions>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </StyledTable>
                </TableWrapper>
              </Col>
            </Row>
          </Section>
        </div>
      </Main>
    </Layout>
  );
}
